import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Send, TriangleAlert } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { formatCount, formatTaka } from '@/utils/number';
import { PCMP_CHANNEL_LABELS, PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { usePublishPcmpMisBatch } from '../../hooks/usePublishPcmpMisBatch';
import type { PcmpMisBatch } from '../../types/pcmpMis.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const commentFormSchema = z.object({
  comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
});

type CommentFormValues = z.infer<typeof commentFormSchema>;

interface PcmpMisPublishDialogProps {
  batch: PcmpMisBatch;
  onClose: () => void;
}

/** Legacy MisRouteBatchPublish: hand the whole staged batch to its payer. */
export function PcmpMisPublishDialog({ batch, onClose }: PcmpMisPublishDialogProps) {
  const form = useForm<CommentFormValues>({
    resolver: zodResolver(commentFormSchema),
    defaultValues: { comment: '' },
  });
  const { mutate, isPending, error } = usePublishPcmpMisBatch(batch.batchNo, onClose);
  const payer = batch.channel ? PCMP_CHANNEL_LABELS[batch.channel] : 'its payer';

  const onSubmit = form.handleSubmit((values) => mutate(values.comment));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Publish Batch #${batch.batchNo}`}
        description={`${formatCount(batch.itemCount, 'proposal')} · ${formatTaka(batch.totalAmount)}`}
        icon={Send}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Publish"
        pendingLabel="Publishing..."
      >
        <div className="flex items-start gap-3 rounded-lg border border-warning-foreground/30 bg-warning px-4 py-3 text-sm text-warning-foreground">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Every proposal in the batch goes to {payer} for payment. MIS cannot roll a published
            batch back; that needs an approved Payment Source Change request.
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Publishing stops if any proposal is on hold or no longer verified. Release or roll those
          back first.
        </p>
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder="Anything the payer should know"
          description="Recorded on each proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
